import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { MasterDataTable, toast, type MasterDataRow } from "@lumanity/ui/primitives";
import {
  SHARED_NOTE,
  SHARED_READ_ONLY,
  projectKeys,
  typeKey,
  useProjectServiceUser,
  useProjectsApiClient,
  type AiPermissionOption,
} from "@lumanity/ui/projects";

const ADMIN_PATH = "/admin/ai-permission-options";
const adminKey = [...projectKeys.aiPermissionOptions(), "admin"] as const;

/**
 * The statements a project creator confirms before AI is used on the project
 * (FR-1-06). The project service has admin endpoints for them but the library
 * has no screen, so this one follows the library's master-data screens:
 * shared statements are read-only unless you're a platform admin.
 */
const AiPermissionOptions = () => {
  const client = useProjectsApiClient();
  const queryClient = useQueryClient();
  const { data: serviceUser } = useProjectServiceUser();
  const isPlatformAdmin = !!serviceUser?.is_platform_admin;

  const { data: options = [], isLoading } = useQuery({
    queryKey: adminKey,
    queryFn: () => client.get<AiPermissionOption[]>(ADMIN_PATH),
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: projectKeys.aiPermissionOptions() });
  const create = useMutation({
    mutationFn: ({ label, isGlobal }: { label: string; isGlobal: boolean }) =>
      client.post(ADMIN_PATH, { key: typeKey(label), label, sort_order: options.length, is_global: isGlobal }),
    onSuccess: invalidate,
  });
  const update = useMutation({
    mutationFn: ({ id, changes }: { id: string; changes: Partial<Pick<AiPermissionOption, "label" | "is_active">> }) =>
      client.put(`${ADMIN_PATH}/${id}`, changes),
    onSuccess: invalidate,
  });
  const remove = useMutation({
    mutationFn: (id: string) => client.delete(`${ADMIN_PATH}/${id}`),
    onSuccess: invalidate,
  });

  const rows: MasterDataRow[] = [...options]
    .sort((a, b) => a.sort_order - b.sort_order)
    .map((o) => {
      const shared = o.app_id === null;
      return {
        id: o.id,
        label: o.label,
        is_active: o.is_active,
        note: shared ? SHARED_NOTE : undefined,
        readOnly: shared && !isPlatformAdmin,
        readOnlyReason: shared && !isPlatformAdmin ? SHARED_READ_ONLY : undefined,
      };
    });

  return (
    <MasterDataTable
      rows={rows}
      valueLabel="Statement"
      isLoading={isLoading}
      onCreate={(label, { toggled }) => create.mutateAsync({ label, isGlobal: toggled })}
      onRename={(id, label) => update.mutateAsync({ id, changes: { label } })}
      onSetActive={(id, is_active) => update.mutateAsync({ id, changes: { is_active } })}
      onDelete={(id) => remove.mutateAsync(id)}
      createToggle={isPlatformAdmin ? { label: "Share with every app" } : undefined}
      onError={(error) => toast({ title: "Something went wrong", description: error.message, variant: "destructive" })}
      emptyText="No AI permission statements yet."
      deleteNote="Projects keep the wording they were confirmed with."
    />
  );
};

export default AiPermissionOptions;
